import { isBranchConstrained } from "../../../auth/sessionAccess.ts";
import React, { useEffect, useState } from "react";
import { useAppSettings } from "../../../hooks/useAppSettings.tsx";
import Layout from "../../Layout.tsx";
import { PieChart } from "@mui/x-charts/PieChart";
import { BarChart } from "@mui/x-charts/BarChart";
import { readBirthdates } from "../../../services/partner.service.ts";
import Title from "../../Title.tsx";
import moment from "moment";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { useParams } from "react-router-dom";
import { toCamelCase } from "../../../hooks/toCameCase.tsx";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/index.ts";
import BranchSelector from "./componentes/BranchSelector.tsx";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

type Props = {};

const BirthDatesReport = (props: Props) => {
  useAppSettings();
  let id;

  const { user } = useSelector((state: RootState) => state.auth);
  const { id: ipParams } = useParams();

  if (isBranchConstrained(user)) {
    id = user.branchId;
  } else {
    id = ipParams;
  }

  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string>("");
  const [branch, setBranch] = useState<any>();
  const [currentBirthDates, setCurrentBirthDates] = useState<any[]>([]);
  const [pastBirthDates, setPastBirthDates] = useState<any[]>([]);
  const [futureBirthDates, setFutureBirthDates] = useState<any[]>([]);
  const [value, setValue] = React.useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const fetchDependencies = async () => {
    try {
      setLoadError("");
      const today = moment();
      const branchId = Number(id);
      const report = await readBirthdates(
        Number.isFinite(branchId) && branchId > 0 ? branchId : undefined,
      );
      setBranch(report?.branch ?? null);
      const partners = Array.isArray(report?.partners) ? report.partners : [];

      const currentBd: any[] = [];
      const pastBd: any[] = [];
      const futureBd: any[] = [];

      partners.forEach((partner: any) => {
        const birthDate = moment(partner.person.birthdate);
        const currentYearBirthDate = moment(birthDate).year(today.year());

        if (today.isSame(currentYearBirthDate, "day")) {
          currentBd.push(partner);
        } else if (today.isAfter(currentYearBirthDate)) {
          pastBd.push(partner);
        } else if (today.isBefore(currentYearBirthDate)) {
          futureBd.push(partner);
        }
      });

      const sortByMonth = (arr: any[]) =>
        [...arr].sort((a, b) => {
          const monthA = moment(a.person.birthdate).month();
          const monthB = moment(b.person.birthdate).month();
          return monthA - monthB;
        });

      const sortPastByDateDesc = (arr: any[]) =>
        [...arr].sort((a, b) => {
          const today = moment();
          const dateA = moment(a.person.birthdate).year(today.year());
          const dateB = moment(b.person.birthdate).year(today.year());
          return dateB.valueOf() - dateA.valueOf(); // más reciente primero
        });

      setCurrentBirthDates(sortByMonth(currentBd));
      setPastBirthDates(sortPastByDateDesc(pastBd));
      setFutureBirthDates(sortByMonth(futureBd));
    } catch (error) {
      console.error("Error fetching dependencies:", error);
      setCurrentBirthDates([]);
      setPastBirthDates([]);
      setFutureBirthDates([]);
      setLoadError("No se pudieron consultar los cumpleaños.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDependencies();
  }, [id]);

  const futureBirthDatesNext15 = React.useMemo(() => {
    const today = moment().startOf("day");
    const days = 15;
    return futureBirthDates.filter((partner: any) => {
      const birthdate = partner.person.birthdate;
      let birthdayThisYear = moment(birthdate).year(today.year());
      if (birthdayThisYear.isBefore(today)) {
        birthdayThisYear = birthdayThisYear.add(1, "year");
      }
      return birthdayThisYear.isBetween(
        today,
        moment(today).add(days, "days"),
        "day",
        "[]"
      );
    });
  }, [futureBirthDates]);

  const isCloser = (birthdate: any) => {
    const today = moment().startOf("day");
    const days = 15;

    let birthdayThisYear = moment(birthdate).year(today.year());

    if (birthdayThisYear.isBefore(today)) {
      birthdayThisYear = birthdayThisYear.add(1, "year");
    }

    const isCloser = birthdayThisYear.isBetween(
      today,
      moment(today).add(days, "days"),
      "day",
      "[]"
    );

    if (isCloser) {
      const remainingDays = birthdayThisYear.diff(today, "days");
      return remainingDays;
    } else {
      return "N/A";
    }
  };

  return (
    <Layout loading={loading}>
      <Title
        baseTitle="Operación"
        basePath="/tactica-operativa"
        activeTitle={`Cumpleaños ${branch?.name || ""}`}
        title={`Reporte de Cumpleaños - ${branch?.name || ""}`}
      />
      <div className="card border-0 m-4 rounded overflow-hidden rounded-3 shadow-sm">
        <div
          className="text-white p-3 d-flex align-items-center"
          style={{
            background: "linear-gradient(245deg, #495057 0%, #212529 100%)",
          }}
        >
          <i className="fa-solid fa-store me-2 fs-14px"></i>
          <h5
            className="mb-0"
            style={{
              fontSize: "12px",
              fontWeight: "600",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            Cambiar Sucursal
          </h5>
        </div>
        <div className="p-4 bg-white">
          <BranchSelector currentId={id} />
        </div>
      </div>
      <div
        className="card border-0 m-4 rounded p-4 shadow-sm bg-white"
        style={{ borderRadius: "15px" }}
      >
        <div className="d-flex align-items-center mb-4 pb-2 border-bottom">
          <div
            style={{
              width: "40px",
              height: "40px",
              backgroundColor: "#ff6b6b20",
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginRight: "12px",
            }}
          >
            <i
              className="fa-solid fa-cake-candles"
              style={{ color: "#ff6b6b" }}
            ></i>
          </div>
          <div>
            <h4 className="mb-0 fw-bold" style={{ color: "#2c3e50" }}>
              {branch?.name}
            </h4>
            <span className="text-muted fs-12px">Resumen de celebraciones</span>
          </div>
        </div>

        <div className="row g-4 align-items-center mb-4">
          <div className="col-lg-5 col-md-12">
            <div
              className="p-4 rounded-4"
              style={{ background: "#fdfdfd", border: "1px solid #f0f0f0" }}
            >
              <h6 className="fw-bold mb-4 text-center text-muted text-uppercase fs-11px">
                Composición de Cumpleaños
              </h6>
              <div className="d-flex justify-content-center">
                <PieChart
                  series={[
                    {
                      data: [
                        {
                          id: 0,
                          value: currentBirthDates.length,
                          label: "Hoy",
                          color: "#ff6b6b",
                        },
                        {
                          id: 1,
                          value: pastBirthDates.length,
                          label: "Pasados",
                          color: "#adb5bd",
                        },
                        {
                          id: 2,
                          value: futureBirthDatesNext15.length,
                          label: "Próximos 15 días",
                          color: "#50B7C0",
                        },
                      ],
                      innerRadius: 70,
                      paddingAngle: 5,
                      cornerRadius: 10,
                    },
                  ]}
                  width={350}
                  height={220}
                  slotProps={{
                    legend: {
                      direction: "column",
                      position: { vertical: "middle", horizontal: "right" },
                      labelStyle: { fontSize: 13 },
                    },
                  }}
                />
              </div>
            </div>
          </div>

          <div className="col-lg-7 col-md-12">
            <div className="row g-3">
              <div className="col-sm-6">
                <div
                  className="p-3 rounded-3 text-center"
                  style={{
                    backgroundColor: "#ff6b6b15",
                    border: "1px solid #ff6b6b30",
                  }}
                >
                  <div className="fs-3 fw-bold" style={{ color: "#ff6b6b" }}>
                    {currentBirthDates.length}
                  </div>
                  <div className="text-muted fs-12px fw-bold text-uppercase">
                    Celebran hoy
                  </div>
                </div>
              </div>
              <div className="col-sm-6">
                <div
                  className="p-3 rounded-3 text-center"
                  style={{
                    backgroundColor: "#50B7C015",
                    border: "1px solid #50B7C030",
                  }}
                >
                  <div className="fs-3 fw-bold" style={{ color: "#50B7C0" }}>
                    {futureBirthDatesNext15.length}
                  </div>
                  <div className="text-muted fs-12px fw-bold text-uppercase">
                    Próximos 15 días
                  </div>
                </div>
              </div>
              <div className="col-12">
                <div
                  className="p-3 rounded-3"
                  style={{
                    background:
                      "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                    border: "1px solid #dee2e6",
                  }}
                >
                  <div className="d-flex align-items-center">
                    <i className="fa-solid fa-circle-info text-primary me-3 fs-3"></i>
                    <div>
                      <div className="fw-bold fs-14px">
                        Mantenimiento de Cultura
                      </div>
                      <p className="mb-0 fs-12px text-muted">
                        Asegúrate de tener listos los detalles para los chilitos
                        que celebran este mes.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Box
          sx={{
            marginTop: "30px",
            "& .MuiTabs-indicator": {
              backgroundColor: "#ff6b6b",
              height: "3px",
            },
            "& .MuiTab-root": {
              fontWeight: "bold",
              fontSize: "14px",
              textTransform: "none",
              color: "#6c757d",
            },
            "& .Mui-selected": { color: "#ff6b6b !important" },
          }}
        >
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="birthdates tabs"
          >
            <Tab
              icon={<i className="fa-solid fa-cake-candles me-2"></i>}
              iconPosition="start"
              label="Cumpleaños de Hoy"
              {...a11yProps(0)}
            />
            <Tab
              icon={<i className="fa-solid fa-calendar-days me-2"></i>}
              iconPosition="start"
              label="Próximos 15 días"
              {...a11yProps(1)}
            />
            <Tab
              icon={<i className="fa-solid fa-clock-rotate-left me-2"></i>}
              iconPosition="start"
              label="Historial Pasados"
              {...a11yProps(2)}
            />
          </Tabs>
        </Box>
        <CustomTabPanel value={value} index={0}>
          <div className="bg-white rounded-4 border overflow-hidden mt-3 shadow-sm">
            <Table>
              <TableHead sx={{ background: "#f8f9fa" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", color: "#495057" }}>
                    Colaborador
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "#495057" }}>
                    Fecha de Nacimiento
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: "bold", color: "#495057" }}
                  >
                    Edad Actual
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentBirthDates.length > 0 ? (
                  currentBirthDates.map(
                    (partner: any, partnerIndex: number) => (
                      <TableRow key={partnerIndex} hover>
                        <TableCell>
                          <div className="d-flex align-items-center">
                            <div
                              style={{
                                width: "40px",
                                height: "40px",
                                borderRadius: "50%",
                                backgroundColor: "#ff6b6b20",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                marginRight: "12px",
                                color: "#ff6b6b",
                                fontWeight: "bold",
                              }}
                            >
                              {partner.person.firstName[0]}
                              {partner.person.lastName[0]}
                            </div>
                            <div>
                              <div
                                className="fw-bold"
                                style={{ color: "#2c3e50" }}
                              >
                                {toCamelCase(
                                  `${partner.person.firstName} ${partner.person.lastName} ${partner.person.secondLastName}`
                                )}
                              </div>
                              <div className="text-muted fs-11px">
                                {partner.position?.name || "Partner"}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <i className="fa-solid fa-calendar-day text-muted me-2"></i>
                          {moment(partner.person.birthdate).format(
                            "DD [de] MMMM"
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <span
                            className="badge rounded-pill px-3 py-2"
                            style={{
                              backgroundColor: "#ff6b6b",
                              fontSize: "12px",
                            }}
                          >
                            {moment().diff(
                              moment(partner.person.birthdate),
                              "years"
                            )}{" "}
                            años
                          </span>
                        </TableCell>
                      </TableRow>
                    )
                  )
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 5 }}>
                      <div className="text-muted">
                        <i className="fa-solid fa-face-smile-wink fs-1 mb-3 d-block opacity-20"></i>
                        <p className="mb-0">
                          {loadError ||
                            "No hay cumpleaños programados para hoy en esta sucursal."}
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CustomTabPanel>
        <CustomTabPanel value={value} index={1}>
          <div className="bg-white rounded-4 border overflow-hidden mt-3 shadow-sm">
            <Table>
              <TableHead sx={{ background: "#f8f9fa" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", color: "#495057" }}>
                    Colaborador
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "#495057" }}>
                    Fecha de Nacimiento
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: "bold", color: "#495057" }}
                  >
                    Días Restantes
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {futureBirthDatesNext15.length > 0 ? (
                  futureBirthDatesNext15.map((partner: any, partnerIndex: number) => (
                    <TableRow key={partnerIndex} hover>
                      <TableCell>
                        <div className="fw-bold" style={{ color: "#2c3e50" }}>
                          {toCamelCase(
                            `${partner.person.firstName} ${partner.person.lastName} ${partner.person.secondLastName}`
                          )}
                        </div>
                        <div className="text-muted fs-11px">
                          Cumplirá{" "}
                          {moment().diff(
                            moment(partner.person.birthdate),
                            "years"
                          ) + 1}{" "}
                          años
                        </div>
                      </TableCell>
                      <TableCell>
                        <i className="fa-solid fa-gift text-muted me-2"></i>
                        {moment(partner.person.birthdate).format(
                          "DD [de] MMMM"
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <span
                          className={`badge rounded-pill px-3 py-2 ${
                            Number(isCloser(partner.person.birthdate)) <= 3
                              ? "bg-danger shadow-sm"
                              : "bg-info text-white"
                          }`}
                          style={{ fontSize: "12px" }}
                        >
                          Faltan {isCloser(partner.person.birthdate)} días
                        </span>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 5 }}>
                      <p className="mb-0 text-muted">
                        {loadError || "No hay cumpleaños en los próximos 15 días."}
                      </p>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CustomTabPanel>
        <CustomTabPanel value={value} index={2}>
          <div className="bg-white rounded-4 border overflow-hidden mt-3 shadow-sm">
            <Table>
              <TableHead sx={{ background: "#f8f9fa" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", color: "#495057" }}>
                    Colaborador
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "#495057" }}>
                    Fecha Histórica
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: "bold", color: "#495057" }}
                  >
                    Edad
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pastBirthDates.length > 0 ? (
                  pastBirthDates.map((partner: any, partnerIndex: number) => (
                    <TableRow key={partnerIndex} hover>
                      <TableCell>
                        <div className="d-flex align-items-center">
                          <div
                            style={{
                              width: "40px",
                              height: "40px",
                              borderRadius: "50%",
                              backgroundColor: "#adb5bd20",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              marginRight: "12px",
                              color: "#6c757d",
                              fontWeight: "bold",
                            }}
                          >
                            {partner.person.firstName?.[0] || ""}
                            {partner.person.lastName?.[0] || ""}
                          </div>
                          <div>
                            <div
                              className="fw-bold"
                              style={{ color: "#2c3e50" }}
                            >
                              {toCamelCase(
                                `${partner.person.firstName || ""} ${partner.person.lastName || ""} ${partner.person.secondLastName || ""}`
                              )}
                            </div>
                            <div className="text-muted fs-11px">
                              {partner.position?.name || "—"}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <i className="fa-solid fa-clock-rotate-left text-muted me-2"></i>
                        {moment(partner.person.birthdate).format("D [de] MMMM")}
                      </TableCell>
                      <TableCell align="right">
                        <span
                          className="badge rounded-pill px-3 py-2"
                          style={{
                            backgroundColor: "#e9ecef",
                            color: "#495057",
                            fontSize: "12px",
                          }}
                        >
                          {moment().diff(
                            moment(partner.person.birthdate),
                            "years"
                          )}{" "}
                          años
                        </span>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 5 }}>
                      <p className="mb-0 text-muted">
                        No hay registros de cumpleaños pasados.
                      </p>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CustomTabPanel>
      </div>
    </Layout>
  );
};

export default BirthDatesReport;
